<template>
  <div class="row">
    <div class="col-xs-12">
      <ag-grid-wrapper
        id="publications-wrapper"
        ref="wrapper"
        :options="gridOptions"
        :separator="true"
        :include-search-box="true"
        :actions="actions"
        store-namespace="data"
        service-namespace="publications"
        card-title-class="text-uppercase"
        card-class="no-border-radius no-shadow"
        needs-update-preprocessing
        @show-publication-form="showForm"
        @requested-update="update"
        @refresh-data="refreshData"
      >
        <template #header-actions-before>
          <div>
            <LmTooltip v-if="!rowData.length">Add titles before saving the title list</LmTooltip>
            <q-btn label="Save title list" size="sm" :disable="!rowData.length" color="positive" @click="saveList" />
          </div>
        </template>
      </ag-grid-wrapper>
    </div>
    <div class="col-xs-12">
      <form-publication
        v-if="showPublicationForm"
        :showing="showPublicationForm"
        :required-fields="updatePublicationRemotely ? ['valid_to', 'valid_from'] : []"
        :fields="columns"
        :values="editMode ? publicationToEdit : null"
        :edit-mode="editMode"
        @save="editMode ? updatePublication($event) : insertPublicationInTitleList($event)"
        @close-modal="resetState"
      />
    </div>
  </div>
</template>

<script>
import { dateRenderer, dateSorter } from '@/lib/generic.utils'
import AgGridWrapper from '@components/ag-grid-helpers/AgGridWrapper'
import LmEntityActions from '@components/ag-grid-helpers/EntityActions'
import FormPublication from '@components/forms/FormPublication'
import { cloneDeep } from 'lodash'

export default {
  name: 'LmPublicationsList',
  components: {
    AgGridWrapper,
    FormPublication
  },
  props: {
    rowData: {
      type: Array,
      required: true
    },
    storeNamespace: {
      type: String,
      default: 'data'
    },
    entity: {
      type: Object,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    updatePublicationRemotely: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      editMode: false,
      showPublicationForm: false,
      publicationToEdit: null,
      indexOfPublication: null
    }
  },
  computed: {
    actions() {
      return [
        {
          label: 'Add new title',
          event: 'show-publication-form',
          type: 'button',
          color: 'tertiary'
        }
      ]
    },
    gridOptions() {
      let opts = {
        rowData: cloneDeep(this.rowData),
        suppressColumnVirtualisation: false,
        context: {
          disableAutoSort: true,
          actions: [
            {
              label: 'Edit',
              onClick: this.edit,
              color: 'tertiary'
            },
            {
              icon: 'trash',
              tooltip: 'Delete',
              onClick: this.delete,
              color: 'faded'
            }
          ]
        },
        columnDefs: []
      }
      this.columns.forEach((columnInfo, index) => {
        let column = {
          headerName: columnInfo.label,
          field: columnInfo.name,
          editable: true,
          cellRenderer: params =>
            columnInfo.data_type === 'date'
              ? `<span>${dateRenderer(params.value)}</span>`
              : `<span>${params.value ? params.value : 'Insert...'}</span>`,
          tooltipValueGetter: params =>
            columnInfo.data_type === 'date'
              ? `${dateRenderer(params.value)}`
              : `${params.value ? params.value : 'Insert...'}`
        }
        if (columnInfo.data_type === 'date') {
          column.cellEditor = 'dateCellEditor'
          column.comparator = dateSorter
        }
        if (index > 2) {
          column.hide = true
        }
        opts.columnDefs.push(column)
      })
      opts.columnDefs.push({
        headerName: 'Actions',
        cellRendererFramework: LmEntityActions
      })
      return opts
    },
    serviceNamespace() {
      return `${this.entity.name}/${this.entity.id}`
    }
  },
  methods: {
    update(event) {
      if (!event.item || !this.updatePublicationRemotely) {
        return
      }
      let data = cloneDeep(event.item)
      data.valid_from = this.formatDateForPost(event.item.valid_from)
      data.valid_to = this.formatDateForPost(event.item.valid_to)
      this.$refs.wrapper.$emit('update', data)
    },
    refreshData() {
      this.$store.dispatch(`titleLists/getSingle`, {
        id: this.entity.id,
        tab: 'title-lists'
      })
    },
    edit(params) {
      this.$set(this, 'publicationToEdit', params.data)
      this.indexOfPublication = params.rowIndex
      this.editMode = true
      this.showPublicationForm = true
    },
    saveList() {
      this.$emit('save')
    },
    showForm() {
      this.showPublicationForm = true
    },
    insertPublicationInTitleList(publication) {
      this.$emit('insert-publication', publication)
      this.$set(this, 'publicationToEdit', null)
      this.editMode = false
    },
    updatePublication(publication) {
      this.editMode = false
      this.$set(this, 'publicationToEdit', null)
      return this.updatePublicationRemotely
        ? this.update({ id: publication.id, item: publication })
        : this.$emit('update-publication', {
            index: this.indexOfPublication,
            publication
          })
    },
    resetState() {
      this.showPublicationForm = false
      this.editMode = false
      this.$set(this, 'publicationToEdit', null)
    },
    delete(params) {
      return this.$emit('delete-publication', params)
    }
  }
}
</script>
